import { IPFSpanType, SpanType } from '../../../pole/integrations';
import { FormModel } from '../..';

export type SpanRole = 'foreSpan' | 'backSpan' | 'otherSpan';

export interface SpanTypeOption {
	text: string;
	value: string;
}

const SPAN_ROLES: SpanRole[] = ['foreSpan', 'backSpan', 'otherSpan'];

const IPF_ROLE_OPTIONS: Record<SpanRole, SpanTypeOption> = {
	foreSpan: { text: 'Fore Span', value: IPFSpanType.FORE_SPAN },
	backSpan: { text: 'Back Span', value: IPFSpanType.BACK_SPAN },
	otherSpan: { text: 'Other', value: IPFSpanType.OTHER },
};

const STANDARD_ROLE_OPTIONS: Record<SpanRole, SpanTypeOption> = {
	foreSpan: { text: 'Fore Span', value: SpanType.NextPole },
	backSpan: { text: 'Back Span', value: SpanType.PreviousPole },
	otherSpan: { text: 'Other', value: SpanType.Other },
};

const STANDARD_BUILDING_OPTION: SpanTypeOption = { text: 'Building', value: SpanType.Building };

/**
 * The style-aware option set a span subform's Type select list must carry.
 * IPF forms use the IPF value set; all other forms use the standard (IKE-calc) set.
 */
export class SpanTypeOptionSet {
	private constructor(
		private readonly roleOptions: Record<SpanRole, SpanTypeOption>,
		private readonly counterpartRoleOptions: Record<SpanRole, SpanTypeOption>,
		private readonly buildingOption: SpanTypeOption | null
	) {}

	private static readonly IPF = new SpanTypeOptionSet(IPF_ROLE_OPTIONS, STANDARD_ROLE_OPTIONS, null);
	private static readonly STANDARD = new SpanTypeOptionSet(
		STANDARD_ROLE_OPTIONS,
		IPF_ROLE_OPTIONS,
		STANDARD_BUILDING_OPTION
	);

	static forForm(form: FormModel): SpanTypeOptionSet {
		return FormModel.isIPFForm(form) ? SpanTypeOptionSet.IPF : SpanTypeOptionSet.STANDARD;
	}

	get requiredRoles(): SpanRole[] {
		return SPAN_ROLES.slice();
	}

	optionForRole(role: SpanRole): SpanTypeOption {
		return this.roleOptions[role];
	}

	/** Building is created by default on standard forms but is not required. Null on IPF. */
	get building(): SpanTypeOption | null {
		return this.buildingOption;
	}

	/** The role an option value satisfies under this style only. */
	roleForValue(value: string): SpanRole | null {
		return SPAN_ROLES.find((role) => this.roleOptions[role].value === value) ?? null;
	}

	/** The role an option value satisfies under either style. */
	roleForAnyStyleValue(value: string): SpanRole | null {
		return (
			SPAN_ROLES.find(
				(role) => this.roleOptions[role].value === value || this.counterpartRoleOptions[role].value === value
			) ?? null
		);
	}

	/** Value replacement applied when linking a span to the opposite pole (fore ↔ back). */
	get linkValueMapping(): Record<string, string> {
		return {
			[this.roleOptions.foreSpan.value]: this.roleOptions.backSpan.value,
			[this.roleOptions.backSpan.value]: this.roleOptions.foreSpan.value,
			[this.roleOptions.otherSpan.value]: this.roleOptions.otherSpan.value,
		};
	}

	/** Maps a wrong-style value to this style's value for the same role, or null. */
	correctedValueFor(value: string): string | null {
		const role = SPAN_ROLES.find((r) => this.counterpartRoleOptions[r].value === value);
		if (role == null || this.roleOptions[role].value === value) {
			return null;
		}
		return this.roleOptions[role].value;
	}

	/** Protected values cannot have their value edited in the form designer. */
	isProtectedValue(value: string): boolean {
		return this.roleForValue(value) != null || value === this.buildingOption?.value;
	}

	/** Building is the only protected option a user may delete. */
	isDeletableProtectedValue(value: string): boolean {
		return value === this.buildingOption?.value;
	}
}
